import React from 'react';
import { Spinner } from 'react-bootstrap';
import { fmt, fmtUnit } from '../utils/helpers';

// unit : respecte le sélecteur d'unité de la section parente

const BORD_H   = '1px solid rgba(255,255,255,0.2)';
const BORD_D   = '1px solid #dedad6';
const BORD_SEP = '2px solid #c8c8c8';

const TH_GRAY = {
  background: '#4a505c', color: '#fff', padding: '6px 10px',
  textAlign: 'center', fontWeight: 700, fontSize: '0.68rem',
  whiteSpace: 'nowrap', borderTop: BORD_H, borderBottom: BORD_H,
  borderLeft: BORD_H, borderRight: BORD_H,
};
const td = (extra = {}) => ({
  padding: '6px 10px', textAlign: 'right', fontSize: '0.76rem',
  whiteSpace: 'nowrap', borderTop: BORD_D, borderBottom: BORD_D,
  borderLeft: BORD_D, borderRight: BORD_D, ...extra,
});

const fmtNb = (v) => Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 1 });

const TableauSynthese = ({ data, loading, fin, onCellClick, budgetCA, unit }) => {
  if (loading) return <div style={{ padding: '20px 0', textAlign: 'center' }}><Spinner /></div>;
  if (!data.length) return null;

  const yr = fin ? `-${fin.slice(2, 4)}` : '';

  const tot = data.reduce((a, r) => ({
    goalV: a.goalV + r.goalV, nbV: a.nbV + r.nbV, caVentes: a.caVentes + r.caVentes,
    realisRec: a.realisRec + r.realisRec,
    goalL: a.goalL + r.goalL, nbL: a.nbL + r.nbL, realisPn: a.realisPn + r.realisPn,
  }), { goalV: 0, nbV: 0, caVentes: 0, realisRec: 0, goalL: 0, nbL: 0, realisPn: 0 });

  const fu = (v) => fmtUnit(v, unit);
  const ecColor = (v) => v > 0 ? '#2e7d32' : v < 0 ? '#c62828' : '#aaa';
  const fmtEc   = (v, isUnit) => v === 0 ? '0' : (v > 0 ? '' : '-') + (isUnit ? fmt(Math.abs(v)) : fu(Math.abs(v)));

  const EcCell = ({ r, b, unit, onFoot }) => {
    if (b == null) return <td style={td({ color: '#ccc', background: onFoot ? '#363c48' : 'inherit' })}>—</td>;
    const v = r - b;
    const col = onFoot
      ? (v > 0 ? '#81c784' : v < 0 ? '#ef9a9a' : '#aaa')
      : ecColor(v);
    return <td style={td({ fontWeight: 700, color: col, background: onFoot ? '#363c48' : 'inherit' })}>{fmtEc(v, unit)}</td>;
  };

  const Td = ({ v, f = fu, bold, color, onClick, foot }) => (
    <td style={td({
      color: color || (foot ? '#fff' : '#333'),
      fontWeight: bold ? 700 : 400,
      cursor: onClick ? 'pointer' : 'default',
      background: foot ? '#363c48' : 'inherit',
    })}
      onClick={onClick}
      title={onClick ? 'Voir le détail' : undefined}
    >{f(v)}</td>
  );
  const TdDash = ({ foot }) => (
    <td style={td({ color: foot ? 'rgba(255,255,255,0.25)' : '#ddd', background: foot ? '#363c48' : 'inherit' })}>—</td>
  );

  const Row = ({ r, i }) => {
    const bg = i % 2 === 0 ? '#fff' : '#f7f6f4';
    const click = (dtype) => onCellClick ? () => onCellClick(dtype, r.type) : undefined;
    return (
      <tr style={{ background: bg }}
        onMouseEnter={e => { e.currentTarget.style.background = '#efefee'; }}
        onMouseLeave={e => { e.currentTarget.style.background = bg; }}>
        <td style={{ ...td({ background: '#e8e7e5', color: '#2a2a2a', fontWeight: 700, textAlign: 'left', borderRight: BORD_SEP, borderLeft: 'none' }) }}>
          {r.type}
        </td>
        <Td v={r.nbV}      f={fmtNb} bold onClick={click('ventes')} />
        <Td v={r.goalV}    f={fmt} color="#888" />
        <EcCell r={r.nbV}  b={r.goalV} unit />
        <Td v={r.caVentes} bold onClick={click('ventes')} />
        <Td v={r.budgetCaV || 0} color="#888" />
        <EcCell r={r.caVentes} b={r.budgetCaV || null} />
        <Td v={r.realisRec} bold color="#d8ae64" onClick={click('recouvrement')} />
        <Td v={r.budgetCaE || 0} color="#888" />
        <EcCell r={r.realisRec} b={r.budgetCaE || null} />
        <Td v={r.nbL}      f={fmt} bold onClick={click('pn')} />
        <Td v={r.goalL}    f={fmt} color="#888" />
        <EcCell r={r.nbL}  b={r.goalL} unit />
        <Td v={r.realisPn} bold onClick={click('pn')} />
        <Td v={r.budgetCaL || 0} color="#888" />
        <EcCell r={r.realisPn} b={r.budgetCaL || null} />
      </tr>
    );
  };

  return (
    <div style={{ marginTop: 0, borderRadius: 6, border: '2px solid #8090a4', overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '0.76rem', width: '100%', minWidth: 760 }}>
        <thead>
          <tr>
            <th style={{ ...TH_GRAY, textAlign: 'left', padding: '7px 14px', background: '#363c48', borderRight: BORD_SEP }}>Désignation</th>
            <th colSpan={3} style={TH_GRAY}>Ventes en unité</th>
            <th colSpan={3} style={TH_GRAY}>Ventes</th>
            <th colSpan={3} style={TH_GRAY}>Encaissements</th>
            <th colSpan={3} style={TH_GRAY}>Livraisons en unité</th>
            <th colSpan={3} style={TH_GRAY}>Livraisons</th>
          </tr>
          <tr>
            <th style={{ ...TH_GRAY, background: '#363c48', textAlign: 'left', padding: '5px 14px', borderRight: BORD_SEP }}> </th>
            {[0,1,2,3,4].map(gi => [
              <th key={`r${gi}`} style={{ ...TH_GRAY, background: '#363c48' }}>{`R${yr}`}</th>,
              <th key={`b${gi}`} style={{ ...TH_GRAY, background: '#363c48' }}>{`B${yr}`}</th>,
              <th key={`e${gi}`} style={{ ...TH_GRAY, background: '#363c48', fontStyle: 'italic' }}>Écart</th>,
            ])}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => <Row key={row.type} r={row} i={i} />)}
        </tbody>
        <tfoot>
          <tr style={{ background: '#363c48' }}>
            <td style={td({ background: '#363c48', color: '#fff', fontWeight: 700, textAlign: 'left', borderRight: BORD_SEP, borderLeft: 'none', borderBottom: '2px solid #c8c8c8' })}>Total</td>
            <Td v={tot.nbV}      f={fmtNb} bold color="#fff" foot />
            <Td v={tot.goalV}    f={fmt} color="rgba(255,255,255,0.6)" foot />
            <EcCell r={tot.nbV}  b={tot.goalV} unit onFoot />
            <Td v={tot.caVentes} bold color="#fff" foot />
            {budgetCA ? <Td v={budgetCA.goalCaV} color="rgba(255,255,255,0.6)" foot /> : <TdDash foot />}
            <EcCell r={tot.caVentes} b={budgetCA?.goalCaV ?? null} onFoot />
            <Td v={tot.realisRec} bold color="#d8ae64" foot />
            {budgetCA ? <Td v={budgetCA.goalCaE} color="rgba(255,255,255,0.6)" foot /> : <TdDash foot />}
            <EcCell r={tot.realisRec} b={budgetCA?.goalCaE ?? null} onFoot />
            <Td v={tot.nbL}      f={fmt} bold color="#fff" foot />
            <Td v={tot.goalL}    f={fmt} color="rgba(255,255,255,0.6)" foot />
            <EcCell r={tot.nbL}  b={tot.goalL} unit onFoot />
            <Td v={tot.realisPn} bold color="#fff" foot />
            {budgetCA ? <Td v={budgetCA.goalCaL} color="rgba(255,255,255,0.6)" foot /> : <TdDash foot />}
            <EcCell r={tot.realisPn} b={budgetCA?.goalCaL ?? null} onFoot />
          </tr>
        </tfoot>
      </table>
      </div>
    </div>
  );
};

export default TableauSynthese;
